import { GridFilterModel } from '@mui/x-data-grid-pro';

import { httpClient, httpGet, HttpMethod } from './http-client';
import { getAllParts } from './parts';
import {
  CreatePickTaskRequest,
  PickConfig,
  PickedConsumable,
  PickedSerial,
  PickTask,
  PickTaskListFilters,
  PrepareRequest,
  PreparedTask,
} from '../@types/picktask.type';

export interface FoundPart {
  id: number;
  partTypeId: number;
  partTypeName?: string;
  serial1?: string;
  conditionCode?: string;
}

// Resolve a scanned serial to its part (incl. partTypeId) via the parts search.
export const findPartBySerial = async (serial: string): Promise<FoundPart | undefined> => {
  const res = await getAllParts(
    { filterModel: { items: [] } as GridFilterModel, page: 0, pageSize: 5 },
    serial
  );
  const raw = (res?.data ?? []) as Array<{
    id: number;
    partTypeId: number;
    partTypeName?: string;
    partType?: { name?: string };
    serial1?: string;
    conditionCode?: string;
  }>;
  const parts: FoundPart[] = raw.map((p) => ({
    id: p.id,
    partTypeId: p.partTypeId,
    partTypeName: p.partTypeName ?? p.partType?.name,
    serial1: p.serial1,
    conditionCode: p.conditionCode,
  }));
  // Only an exact serial match resolves a part; a near-miss must NOT fall back to
  // the first search result (that would misattribute the picked serial/qty to an
  // unrelated part). Callers handle undefined ("serial not found").
  const exact = parts.find((p) => (p.serial1 ?? '').toLowerCase() === serial.toLowerCase());
  return exact;
};

// POST /picktask/prepare - compute pick-task(s) (sources + snapshot) from replenishment configs.
export const preparePickTask = (data: PrepareRequest): Promise<PreparedTask[]> =>
  httpClient<PreparedTask[]>('/picktask/prepare', HttpMethod.Post, data);

// POST /picktask - create a pick task allocated to a user.
export const createPickTask = (data: CreatePickTaskRequest): Promise<PickTask> =>
  httpClient<PickTask>('/picktask', HttpMethod.Post, data);

// GET /picktask - list (filters: allocatedToUserId, status, toLocationNodeId).
export const getPickTasks = (
  filters?: PickTaskListFilters,
  signal?: AbortSignal
): Promise<PickTask[]> => {
  const params = new URLSearchParams();
  if (filters?.allocatedToUserId != null)
    params.append('allocatedToUserId', String(filters.allocatedToUserId));
  if (filters?.status) params.append('status', filters.status);
  if (filters?.toLocationNodeId != null)
    params.append('toLocationNodeId', String(filters.toLocationNodeId));
  const qs = params.toString();
  return httpGet<PickTask[]>(`/picktask${qs ? `?${qs}` : ''}`, signal);
};

// GET /picktask/:id - detail with history/sources/shipments/issues.
export const getPickTaskById = (id: number, signal?: AbortSignal): Promise<PickTask> =>
  httpGet<PickTask>(`/picktask/${id}`, signal);

// PUT /picktask/:id/cancel - BFF fills cancelledBy from the JWT.
export const cancelPickTask = (id: number): Promise<PickTask> =>
  httpClient<PickTask>(`/picktask/${id}/cancel`, HttpMethod.Put, {});

// PUT /picktask/:id/source - change the source (from) location; null clears it.
export const updatePickTaskSource = (
  id: number,
  fromLocationNodeId: number | null
): Promise<PickTask> =>
  httpClient<PickTask>(`/picktask/${id}/source`, HttpMethod.Put, { fromLocationNodeId });

// PUT /picktask/:id/scans - persist the serials scanned + consumable quantities picked.
export const savePickTaskScans = (
  id: number,
  pickedSerials: PickedSerial[],
  pickedConsumables: PickedConsumable[]
): Promise<PickTask> =>
  httpClient<PickTask>(`/picktask/${id}/scans`, HttpMethod.Put, {
    pickedSerials,
    pickedConsumables,
  });

// POST /picktask/:id/issue - record a pick issue.
export const addPickIssue = (
  id: number,
  data: { shipmentId?: number; partTypeId?: number; quantity: number; notes?: string }
): Promise<unknown> => httpClient(`/picktask/${id}/issue`, HttpMethod.Post, data);

// GET /pickconfig - configurable pick settings (default source location).
export const getPickConfig = (signal?: AbortSignal): Promise<PickConfig> =>
  httpGet<PickConfig>('/pickconfig', signal);

// PUT /pickconfig - set the default source location.
export const updatePickConfig = (defaultSourceNodeId: number | null): Promise<PickConfig> =>
  httpClient<PickConfig>('/pickconfig', HttpMethod.Put, { defaultSourceNodeId });
